import React from "react";
import { useAnalytics } from "../../contexts/AnalyticsContext";

// Import existing Pista theme styles
import "../../styles/pista-theme.css";
//import { SEARCH_ICON, TARGET_ICON, MECHANICS_ICON, CATEGORIES_ICON, ACTIVITY_ICON, CTA_ICON } from "../../constants/icons";

/**
 * Single insight card with CTA buttons
 *
 * @param {Object} props
 * @param {Object} props.insight - The insight data
 * @param {string} props.insight.type - Insight type (trend, gap, identity, collection_feature, collection_activity, cta)
 * @param {string} props.insight.label - Insight label
 * @param {string} props.insight.text - Full text (for non-compact mode)
 * @param {string} props.insight.short_text - Short text (for compact mode)
 * @param {Object} props.insight.cta_context - CTA configuration
 * @param {boolean} props.compact - Whether to show compact version
 * @param {Function} props.onKeepGoing - Callback for primary CTA
 * @param {Function} props.onDismiss - Callback for Dismiss button
 * @param {boolean} props.isActive - Whether this card is currently active in carousel
 */
function InsightCard({
  insight,
  compact = true,
  onKeepGoing,
  onDismiss,
  isActive = true
}) {
  const { trackClick } = useAnalytics();

  if (!insight) return null;

  const { text, short_text, cta_context } = insight;

  // Use short_text in compact mode, full text otherwise
  const displayText = compact ? short_text || text : text || short_text;

  // Get CTA configurations
  const keepGoingCTA = cta_context?.keep_going;

  // Determine if we have a valid primary CTA
  const hasKeepGoing = keepGoingCTA && (keepGoingCTA.action || keepGoingCTA.destination);

/*   // Get icon based on insight type
  const getIcon = () => {
    switch (type) {
      case 'trend':
        return '📈';
      case 'gap':
        return SEARCH_ICON;
      case 'identity':
        return TARGET_ICON;
      case 'collection_feature':
        return label === 'owned_mechanic' ? MECHANICS_ICON : CATEGORIES_ICON;
      case 'collection_activity':
        return ACTIVITY_ICON;
      case 'cta':
        return CTA_ICON;
      case 'summary':
        return CTA_ICON;
      case 'explore':
        return CTA_ICON;
      default:
        return CTA_ICON;
    }
  }; */

  const primaryCtaLabel = keepGoingCTA?.label || "Find more like this";

  const handleKeepGoing = (e) => {
    e.preventDefault();
    trackClick('insight_cta_click', {
      insight_label: insight.label,
      insight_type: insight.type,
      cta_action: keepGoingCTA?.action,
      cta_label: primaryCtaLabel,
    }, e.currentTarget);
    if (onKeepGoing && keepGoingCTA) {
      onKeepGoing(keepGoingCTA, insight);
    }
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    trackClick('insight_dismissed', {
      insight_label: insight.label,
      insight_type: insight.type,
    }, e.currentTarget);
    if (onDismiss) {
      onDismiss(insight);
    }
  };

  // Only render CTA row when card is active in the carousel
  const showCTARow = isActive && (hasKeepGoing || onDismiss);

  return (
    <div
      className="insight-card"
      style={{
        background: "var(--bg-secondary, #fafbfc)",
        borderRadius: "12px",
        padding: compact ? "12px 16px" : "20px 24px",
        color: "var(--text-primary, #333)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        border: "1px solid var(--border-color, #ddd)",
        transition: "all 0.3s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Insight Content */}
      <div style={{ flex: "0 0 1%" }}>
        <p style={{
          margin: 0,
          fontSize: compact ? "14px" : "16px",
          lineHeight: 1.4,
          fontWeight: 500
        }}>
          {displayText}
        </p>
      </div>

      {/* CTA Buttons — primary action + dismiss (always show dismiss on active cards) */}
      {showCTARow && (
        <div style={{
          display: "flex",
          gap: "8px",
          marginTop: compact ? "12px" : "16px",
          paddingTop: compact ? "12px" : "16px",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "4px"
        }}>
          {hasKeepGoing && (
            <button
              onClick={handleKeepGoing}
              data-track-handled="true"
              className="insight-cta-button insight-cta-primary"
              style={{
                flex: 1,
                padding: compact ? "6px 12px" : "8px 16px",
                background: "var(--pista-green-primary, #2E7D32)",
                color: "var(--pista-button-text, #FFFFFF)",
                border: "none",
                borderRadius: "6px",
                fontSize: compact ? "12px" : "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "var(--pista-green-hover, #388E3C)";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "var(--pista-green-primary, #2E7D32)";
                e.target.style.transform = "translateY(0)";
              }}
            >
              {primaryCtaLabel}
            </button>
          )}

          <button
            onClick={handleDismiss}
            data-track-handled="true"
            className="insight-cta-button insight-cta-secondary"
            style={{
              padding: compact ? "6px 12px" : "8px 16px",
              background: "transparent",
              color: "var(--text-secondary, #6b7280)",
              border: "1px solid var(--border-color, #ddd)",
              borderRadius: "6px",
              fontSize: compact ? "12px" : "13px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--bg-tertiary, #f3f4f6)";
              e.target.style.color = "var(--text-primary, #333)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = "var(--text-secondary, #6b7280)";
            }}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

export default InsightCard;
